<template>
    <wrapper-features-templates-operations
    :activeStep="activeStep"
    :scrollValue="scrollValue"
    :footerButtonsParams="footerButtonsParams.filter(obj => !obj.hidden)"
    :showFooter="activeStep < 3"
    :showOverlayLoader="showOverlayLoader"
    :stepperParams="stepperParams"
    :feedbackOperationData="headerOperationData"
    :showStepper="activeStep <= stepperParams.length"
    :tokenTitleAppend="t('cards.activate.tokenTitleAppend')"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, activationRequest)"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <component
        :is="getActiveStepComponent"
        :isMobile="getMobileAppState"
        :clearFormErrors="clearFormErrors"
        :formData="formData"
        :mandatoryList="mandatoryList"
        :cardValidateResponse="cardValidateResponse"
        :responseData="responseData"
        :showMandatoryErrors="showMandatoryErrors"
        @formDataChange="formDataChangeHandler"
        @formErrorsCleared="clearFormErrors = false"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @scrollTo="scrollValue = $event"
        @validateCardPan="validateCardRequest"
        @newActivation="newActivation"/>
    </wrapper-features-templates-operations>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent } from 'vue'
/* Assets & Helpers */
import { atLeastOneIsEmpty, clearObjectStrings } from '@/assets/scripts/objects'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { validateCard, cardActivation } = mapActions(moduleMapper.CARDS_ACTIVATE)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* Global Data */
const showOverlayLoader = ref(false)
const scrollValue = ref(null)
const headerOperationData = ref(null)
const cardValidateResponse = ref(null)
const responseData = ref()

/*************************************************
*                                                *
*                    Steps Data                  *
*                                                *
*************************************************/
const activeStep = ref(1)
const stepperParams = ref([
    { icon: 'edit-pencil', label: t('cards.activate.stepper.data') },
    { icon: 'list', label: t('cards.activate.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/cards/activate/Step' + stepNumber))
})

/*************************************************
*                                                *
*                Form Logic Data                 *
*                                                *
*************************************************/
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

const formData = ref({
    cardPan: '',
    cardValidated: false
})

const mandatoryList = ref([
    'cardPan',
    'cardValidated'
])

const formDataChangeHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue
    if (field === 'cardPan') {
        cardValidateResponse.value = false
        formData.value.cardValidated = false
    }
}

/*************************************************
*                                                *
*                     Requests                   *
*                                                *
*************************************************/
const validateCardRequest = async () => {
	showOverlayLoader.value = true

    const payload = formData.value.cardPan

	try {
        const res = await validateCard(payload)
        cardValidateResponse.value = res.data.data
        showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        if (error.response && error.response.data && errorForInput(error.response.data)) return
        errorHandler(error.response.data, validateCardRequest)
	}
}

/*
* Validates if it's an error to display on input, and if so applies info in our object for that purpose,
* also returns a bolean, to prevent toast from appearing
*/
const errorForInput = error => {
    if (error.code !== 'card_not_found') return false

    cardValidateResponse.value = {
        valid: false,
        additionalErrorMessage: error.message
    }

    showOverlayLoader.value = false
    return true
}

const activateCardRequest = async () => {
	showOverlayLoader.value = true

    const payload = formData.value.cardPan

	try {
        await cardActivation(payload)
        showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, activateCardRequest)
	}
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const clearFormData = () => {
    clearFormErrors.value = true
    formData.value = clearObjectStrings(formData.value)
    formData.value.cardValidated = false
    headerOperationData.value = null
    cardValidateResponse.value = null
    responseData.value = null
}

const validateInputs = () => {
    if (!formData.value.cardValidated && !!formData.value.cardPan.length) {
        errorHandler({
            message: t('cards.activate.noValidateError'),
            dismissToastBtnText: t('cards.activate.noValidateLabel')
        })
        return
    }

    atLeastOneIsEmpty(formData.value, mandatoryList.value) ? showMandatoryErrors.value = true : activeStep.value = 2
}

/* Footer Buttons Data */
const footerButtonsParams = computed(() => {
    return [
        {
            label: t('cards.activate.footerButtons.clear'),
            callbackFunction: clearFormData,
            hidden: activeStep.value !== 1
        }, {
            label: t('cards.activate.footerButtons.forward'),
            callbackFunction: validateInputs,
            icon: 'arrow-right-full',
            iconPosition: 'right',
            buttonType: 'primary',
            hidden: activeStep.value !== 1
        }, {
            label: t('cards.activate.footerButtons.edit'),
            icon: 'arrow-left-full',
            callbackFunction: () => { activeStep.value = 1 },
            hidden: activeStep.value !== 2
        }, {
            label: t('cards.activate.footerButtons.validate'),
            callbackFunction: activateCardRequest,
            icon: 'check-line',
            iconPosition: 'right',
            buttonType: 'primary',
            hidden: activeStep.value !== 2
        }
    ]
})

/* New Activation Button Data for Step3 */
const newActivation = () => {
    clearFormData()
    activeStep.value = 1
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = data => {
    activeStep.value++
    responseData.value = data

    const buttonsTopData = {
        iconsColor: data.errorLevel === 'INFO' ? 'var(--positive)' : 'var(--warning)',
        emailAction: 'sendOperationEmailNotification',
        smsAction: 'sendOperationSMSNotification',
        sendPayload: { operationId: data.data.customerOperation.id },
        downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: data.data.customerOperation.id } }),
        operationId: data.data.operationId
    }

    headerOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: data.data.hasReceipt ? buttonsTopData : null
    }
}

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { activeStep.value = 1 })
	showFeedbackToast.value = true
}
</script>

<style lang="scss" scoped>
</style>
